import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socket from '../services/socket';
import API_URL from '../config/api';

// Konstanty pro časování
const COUNTDOWN_DAYS = 7; // Počet dní do spuštění aukce
const COUNTDOWN_SECONDS = 10;

const AuctionRegistration = ({ auction, onRegister, onStatusChange }) => {
  const { currentUser } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [participantsCount, setParticipantsCount] = useState(auction.participants?.length || 0);
  const [currentPrice, setCurrentPrice] = useState(auction.currentBid);
  const [customBidAmount, setCustomBidAmount] = useState(auction.currentBid + 1000);
  const [timeToStart, setTimeToStart] = useState('');
  const [countdownTime, setCountdownTime] = useState('');

  // Funkce pro výpočet zbývajícího času
  const calculateTimeLeft = (endTime) => {
    const now = new Date().getTime();
    const targetTime = new Date(endTime).getTime();
    const difference = targetTime - now;

    if (difference <= 0) {
      // Když odpočet skončí, změníme status na 'live'
      socket.emit('auctionStatusChanged', {
        auctionId: auction._id,
        newStatus: 'live'
      });
      return 'Aukce začíná...';
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  // Callback pro aktualizaci příhozů
  const handleBidUpdate = useCallback((data) => {
    if (data.auctionId === auction._id) {
      console.log('Nový příhoz:', data);
      setCurrentPrice(data.amount);
      setCustomBidAmount(data.amount + 1000);
    }
  }, [auction._id]);

  // Callback pro změnu stavu
  const handleStatusChange = useCallback((data) => {
    if (data.auctionId === auction._id) {
      console.log('Změna stavu aukce:', data);
      onStatusChange(data.status, data.additionalData);
    }
  }, [auction._id, onStatusChange]);

  // Callback pro odpočet
  const handleCountdownUpdate = useCallback((data) => {
    if (data.auctionId === auction._id) {
      console.log('Aktualizace odpočtu:', data);
      setCountdownTime(data.timeLeft);
    }
  }, [auction._id]);

  // Socket.IO připojení
  useEffect(() => {
    if (!auction._id) return;

    // Připojení k aukci
    socket.emit('joinAuction', {
      auctionId: auction._id,
      userId: currentUser?.uid
    });

    // Registrace event listenerů
    socket.on('bidUpdated', handleBidUpdate);
    socket.on('auctionStatusChanged', handleStatusChange);
    socket.on('countdownUpdated', handleCountdownUpdate);

    // Cleanup
    return () => {
      socket.emit('leaveAuction', {
        auctionId: auction._id,
        userId: currentUser?.uid
      });
      socket.off('bidUpdated', handleBidUpdate);
      socket.off('auctionStatusChanged', handleStatusChange);
      socket.off('countdownUpdated', handleCountdownUpdate);
    };
  }, [
    auction._id,
    currentUser?.uid,
    handleBidUpdate,
    handleStatusChange,
    handleCountdownUpdate
  ]);

  // Handler pro nového účastníka
  const handleParticipantJoined = (data) => {
    if (data.auctionId === auction._id) {
      setParticipantsCount(data.participantsCount);
      
      if (data.participantsCount >= auction.requiredParticipants) {
        const startTime = new Date();
        const countdownEndTime = new Date(startTime.getTime() + (COUNTDOWN_SECONDS * 1000));
        
        onStatusChange('countdown', {
          countdownEndTime: countdownEndTime.toISOString()
        });
      }
    }
  };

  // Aktualizace customBidAmount při změně currentPrice
  useEffect(() => {
    setCustomBidAmount(currentPrice + 1000);
  }, [currentPrice]);

  // Upravená funkce pro registraci
  const handleRegistration = async () => {
    if (!currentUser) {
      setError('Pro registraci do aukce se musíte přihlásit');
      return;
    }

    setIsRegistering(true);
    setError('');

    try {
      await onRegister();
      
      socket.emit('userRegistered', {
        auctionId: auction._id,
        userId: currentUser.uid,
        participantsCount: participantsCount + 1
      });
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleBid = async (amount) => {
    if (!currentUser) {
      setError('Pro příhoz musíte být přihlášeni');
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      
      const requestData = {
        auctionId: auction._id,
        amount
      };

      // Použijeme plnou URL serveru pro development
      const bidResponse = await fetch(`${API_URL}/api/bids`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      console.log('Response status:', bidResponse.status);
      console.log('Response headers:', Object.fromEntries(bidResponse.headers.entries()));

      const responseText = await bidResponse.text();
      console.log('Raw response:', responseText);

      if (!bidResponse.ok) {
        throw new Error(`Server vrátil status ${bidResponse.status}: ${responseText}`);
      }

      const bidData = JSON.parse(responseText);
      
      // Emit socket event pro aktualizaci příhozů
      socket.emit('bidUpdated', {
        auctionId: auction._id,
        amount: bidData.amount,
        userId: bidData.userId,
        createdAt: bidData.createdAt
      });

      setCurrentPrice(bidData.amount);
      setCustomBidAmount(bidData.amount + 1000);
    } catch (error) {
      console.error('Detailní chyba:', error);
      setError(`Chyba při příhozu: ${error.message}`);
    }
  };

  const handleQuickBid = async () => {
    try {
      await handleBid(currentPrice + 1000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCustomBid = async (e) => {
    e.preventDefault();
    try {
      await handleBid(customBidAmount);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg transition-all duration-300">
      <h2 className="text-2xl font-bold mb-4 text-white">Registrace do aukce</h2>
      
      {auction.status === 'countdown' && (
        <div className="text-center mb-6 bg-blue-900/50 p-4 rounded-lg animate-pulse">
          <p className="text-gray-300 mb-2">Začátek aukce za:</p>
          <div className="text-3xl font-bold text-blue-400">
            {countdownTime}
          </div>
        </div>
      )}
      
      <div className="mb-4 bg-gray-700/50 p-4 rounded-lg">
        <p className="text-gray-300">
          Počet registrovaných účastníků: 
          <span className="text-blue-400 font-bold ml-2 text-lg">
            {participantsCount}
          </span>
        </p>
      </div>

      <div className="mb-4 bg-gray-700/50 p-4 rounded-lg">
        <p className="text-white">
          Aktuální cena: 
          <span className="text-green-400 font-bold ml-2">
            {currentPrice.toLocaleString()} Kč
          </span>
        </p>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded mb-4 animate-shake">
          {error}
        </div>
      )}

      {currentUser && !auction.participants?.includes(currentUser.uid) && (
        <button
          onClick={handleRegistration}
          disabled={isRegistering}
          className={`w-full bg-blue-600 text-white py-3 px-4 rounded-lg
            hover:bg-blue-700 transform hover:scale-105 transition-all duration-300
            ${isRegistering ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isRegistering ? 'Registruji...' : 'Registrovat do aukce'}
        </button>
      )}

      {currentUser && auction.participants?.includes(currentUser.uid) && (
        <div className="space-y-4">
          <div className="text-green-400 font-semibold text-center mb-4 bg-green-900/30 p-3 rounded-lg">
            Jste zaregistrováni v této aukci
          </div>
          
          <button
            onClick={handleQuickBid}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg
              hover:bg-green-700 transform hover:scale-105 transition-all duration-300"
          >
            Přihodit {(1000).toLocaleString()} Kč
          </button>

          <form onSubmit={handleCustomBid} className="flex space-x-2">
            <input
              type="number"
              value={customBidAmount}
              onChange={(e) => setCustomBidAmount(Number(e.target.value))}
              min={currentPrice + 1000}
              step="1000"
              className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg
                border border-gray-600 focus:ring-2 focus:ring-blue-500 
                focus:border-transparent transition-all duration-300"
              placeholder="Vlastní částka"
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg
                hover:bg-blue-700 transform hover:scale-105 
                transition-all duration-300 whitespace-nowrap"
            >
              Přihodit
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AuctionRegistration; 